import { Badge } from '@synoptic/ui-kit/badge/badge.js';
import { IconButton } from '@synoptic/ui-kit/button/icon-button.js';
import { Checkbox } from '@synoptic/ui-kit/checkbox/checkbox.js';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@synoptic/ui-kit/collapsible/collapsible.js';
import { CheckIcon } from '@synoptic/ui-kit/icons/react/check.js';
import { EyeClosedIcon } from '@synoptic/ui-kit/icons/react/eye-closed.js';
import { InfoCircleIcon } from '@synoptic/ui-kit/icons/react/info-circle.js';
import { NavArrowDownIcon } from '@synoptic/ui-kit/icons/react/nav-arrow-down.js';
import { PlusIcon } from '@synoptic/ui-kit/icons/react/plus.js';
import { TrashIcon } from '@synoptic/ui-kit/icons/react/trash.js';
import { Input } from '@synoptic/ui-kit/input/input.js';
import { LinkButton } from '@synoptic/ui-kit/link-button/link-button.js';
import { normalizeTagForRegex } from '@synoptic/ui-kit/multi-tag/normalize-tag-for-regex.js';
import { SelectionTag } from '@synoptic/ui-kit/selection-tag/selection-tag.js';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@synoptic/ui-kit/tooltip/tooltip.js';
import { ClipboardEvent, KeyboardEvent, useState } from 'react';
import { StreamPostType } from '@/__generated__/schema-types';
import { POST_TYPE_OPTIONS } from './stream-post-types';
import {
  isStreamTargetsCollapsed,
  setStreamTargetsCollapsed,
} from './targets-collapse-storage';
import {
  addOption,
  addOptionTarget,
  chevron,
  countBadge,
  fieldError,
  noShrinkChipBadge,
  postTypeTag,
  postTypesLabel,
  postTypesRow,
  removalAlert,
  removedGroupHeader,
  removedTargetRow,
  rowRight,
  streamCardHeader,
  streamField,
  streamName as streamNameText,
  targetItemLabel,
  targetItemText,
  targetRemove,
  targetRow,
  searchRow,
  targetsBox,
  targetsEmpty,
  targetsFoot,
  targetsGrid,
  targetsGroup,
  targetsHeader,
  targetsHeaderLabel,
  targetsHint,
} from './custom-targets-section.css';

const ADD_KEYS = ['Enter', ',', ' '];

interface RemovedTarget {
  name: string;
  wasUsed: boolean;
}

interface StreamTargetsFieldProps {
  streamId: string;
  streamName: string;
  allTargets: string[];
  onAllTargetsChange: (targets: string[]) => void;
  /** What the stream has saved; anything else was added in this form. */
  savedTargets: string[];
  /** The subset this list uses; undefined means all of them. */
  explicitTargets?: string[];
  onExplicitTargetsChange: (targets: string[] | undefined) => void;
  /** 0 when the stream sets no quota. */
  quota: number;
  onBuyMore?: () => void;
  instruction?: string | null;
  targetValidationRegex?: string | null;
  excludedPostTypes?: StreamPostType[];
  onExcludedPostTypesChange?: (types: StreamPostType[]) => void;
}

export const StreamTargetsField = ({
  streamId,
  streamName,
  allTargets,
  onAllTargetsChange,
  savedTargets,
  explicitTargets,
  onExplicitTargetsChange,
  quota,
  onBuyMore,
  instruction,
  targetValidationRegex,
  excludedPostTypes,
  onExcludedPostTypesChange,
}: StreamTargetsFieldProps) => {
  const [open, setOpen] = useState(() => !isStreamTargetsCollapsed(streamId));
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Restore has to know whether a deleted target was in this list.
  const [removed, setRemoved] = useState<RemovedTarget[]>([]);

  const usedHere = explicitTargets ?? allTargets;
  const usedHereSet = new Set(usedHere);
  const usedAll =
    allTargets.length > 0 && usedHere.length === allTargets.length;
  const atQuota = quota > 0 && allTargets.length >= quota;

  const query = draft.trim().toLowerCase();
  const visible = query
    ? allTargets.filter((target) => target.toLowerCase().includes(query))
    : allTargets;
  // One input for both, so the typed name stays on offer until it exists.
  const canAdd =
    query.length > 0 &&
    !atQuota &&
    !allTargets.some((target) => target.toLowerCase() === query);
  const included = visible.filter((target) => usedHereSet.has(target));
  const excluded = visible.filter((target) => !usedHereSet.has(target));

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    setStreamTargetsCollapsed(streamId, !next);
  };

  const setUsedHere = (next: string[]) =>
    onExplicitTargetsChange(
      allTargets.length > 0 && next.length === allTargets.length
        ? undefined
        : next,
    );

  const toggle = (target: string) =>
    setUsedHere(
      usedHereSet.has(target)
        ? usedHere.filter((value) => value !== target)
        : [...usedHere, target],
    );

  // A subset holding every target is the "all targets" default.
  const commit = (nextAll: string[], nextUsed: string[]) => {
    onAllTargetsChange(nextAll);
    onExplicitTargetsChange(
      nextUsed.length === nextAll.length ? undefined : nextUsed,
    );
  };

  const deleteTarget = (target: string) => {
    const wasUsed = usedHereSet.has(target);
    commit(
      allTargets.filter((value) => value !== target),
      usedHere.filter((value) => value !== target),
    );
    // Only a saved target has anything to lose.
    if (savedTargets.includes(target)) {
      setRemoved((current) =>
        current.some((entry) => entry.name === target)
          ? current
          : [...current, { name: target, wasUsed }],
      );
    }
  };

  const restore = () => {
    const back = removed.filter((entry) => entry.wasUsed).map((e) => e.name);
    commit(
      [...allTargets, ...removed.map((entry) => entry.name)],
      [...usedHere, ...back],
    );
    setRemoved([]);
  };

  const addTargets = (values: string[]) => {
    let regex: RegExp | undefined;
    if (targetValidationRegex) {
      try {
        regex = new RegExp(targetValidationRegex);
      } catch {
        regex = undefined;
      }
    }

    const known = new Set(allTargets.map((target) => target.toLowerCase()));
    const added: string[] = [];
    let nextError: string | null = null;

    for (const value of values) {
      const target = normalizeTagForRegex(value.trim(), regex);
      if (!target) continue;
      if (known.has(target.toLowerCase())) {
        nextError = `${target} is already tracked`;
        continue;
      }
      if (regex && !regex.test(target)) {
        nextError = `${target} doesn't match the required format`;
        continue;
      }
      if (quota > 0 && allTargets.length + added.length >= quota) {
        nextError = `This subscription covers ${quota} targets`;
        break;
      }
      known.add(target.toLowerCase());
      added.push(target);
    }

    setError(nextError);
    if (added.length === 0) return;

    setDraft('');
    commit([...allTargets, ...added], [...usedHere, ...added]);
    setRemoved((current) =>
      current.filter((entry) => !added.includes(entry.name)),
    );
  };

  const onDraftKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!ADD_KEYS.includes(event.key)) return;
    event.preventDefault();
    addTargets([event.currentTarget.value]);
  };

  const onDraftPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    addTargets(event.clipboardData.getData('text').split(/[\s,]+/));
  };

  const renderRows = (targets: string[], isExcluded: boolean) =>
    targets.map((target) => (
      <div key={target} className={isExcluded ? removedTargetRow : targetRow}>
        <label className={targetItemLabel}>
          <Checkbox
            checked={!isExcluded}
            aria-label={`Use ${target} in this list`}
            onCheckedChange={() => toggle(target)}
          />
          <span className={targetItemText}>{target}</span>
        </label>
        <IconButton
          type="button"
          size="xSmall"
          variant="ghost"
          className={targetRemove}
          aria-label={`Delete ${target} from ${streamName}`}
          onClick={() => deleteTarget(target)}
        >
          <TrashIcon />
        </IconButton>
      </div>
    ));

  return (
    <div className={streamField}>
      <Collapsible open={open} onOpenChange={onOpenChange}>
        <CollapsibleTrigger asChild>
          <button type="button" className={streamCardHeader}>
            <span className={streamNameText}>{streamName}</span>
            <Badge color="neutral" className={countBadge}>
              {usedHere.length}/{allTargets.length}
            </Badge>
            <span className={rowRight}>
              {excludedPostTypes?.length ? (
                <Badge color="brand" className={noShrinkChipBadge}>
                  <EyeClosedIcon width={12} height={12} />
                  {excludedPostTypes.length} hidden
                </Badge>
              ) : null}
              <NavArrowDownIcon width={16} height={16} className={chevron} />
            </span>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className={targetsBox}>
            {excludedPostTypes && onExcludedPostTypesChange ? (
              <div
                role="group"
                aria-label={`Post types shown from ${streamName}`}
                className={postTypesRow}
              >
                <span className={postTypesLabel}>Show</span>
                {POST_TYPE_OPTIONS.map(({ type, label }) => {
                  const shown = !excludedPostTypes.includes(type);
                  return (
                    <SelectionTag
                      key={type}
                      size="sm"
                      checked={shown}
                      className={postTypeTag}
                      onClick={() =>
                        onExcludedPostTypesChange(
                          shown
                            ? [...excludedPostTypes, type]
                            : excludedPostTypes.filter(
                                (value) => value !== type,
                              ),
                        )
                      }
                    >
                      {shown ? <CheckIcon width={14} height={14} /> : null}
                      {label}
                    </SelectionTag>
                  );
                })}
              </div>
            ) : null}
            <div className={searchRow}>
              <Input
                placeholder="Search targets, or type a new one to add"
                aria-label={`Search or add targets in ${streamName}`}
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                  setError(null);
                }}
                onKeyDown={onDraftKeyDown}
                onPaste={onDraftPaste}
              />
            </div>

            <div className={targetsHeader}>
              <span className={targetsHeaderLabel}>
                Targets
                {instruction ? (
                  <Tooltip delayDuration={100}>
                    <TooltipTrigger asChild>
                      <span
                        className={targetsHint}
                        tabIndex={0}
                        aria-label="Author's instruction"
                      >
                        <InfoCircleIcon width={14} height={14} />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent
                      title="Author's instruction"
                      description={instruction}
                    />
                  </Tooltip>
                ) : null}
              </span>
              <Checkbox
                checked={
                  usedAll ? true : usedHere.length > 0 ? 'indeterminate' : false
                }
                disabled={allTargets.length === 0}
                aria-label={`Use every target of ${streamName}`}
                onCheckedChange={() => setUsedHere(usedAll ? [] : allTargets)}
              />
            </div>

            {canAdd ? (
              <button
                type="button"
                className={addOption}
                onClick={() => addTargets([draft])}
              >
                <PlusIcon width={14} height={14} />
                Add <span className={addOptionTarget}>{draft.trim()}</span>
              </button>
            ) : null}

            <div className={targetsGrid}>
              {visible.length > 0 ? (
                <>
                  {renderRows(included, false)}
                  {excluded.length > 0 ? (
                    <div
                      role="group"
                      aria-label="Removed from this list"
                      data-testid="removed-list-targets"
                      className={targetsGroup}
                    >
                      <div className={removedGroupHeader}>
                        Removed from this list
                      </div>
                      {renderRows(excluded, true)}
                    </div>
                  ) : null}
                </>
              ) : (
                <div className={targetsEmpty}>
                  {query ? 'No targets found' : 'No targets in this stream yet'}
                </div>
              )}
            </div>
          </div>

          {error ? <p className={fieldError}>{error}</p> : null}

          <div className={targetsFoot}>
            <span>
              {usedHere.length} of {allTargets.length} used in this list
            </span>
            {quota > 0 ? (
              <span>
                {allTargets.length} / {quota}
                {onBuyMore ? (
                  <>
                    {' · '}
                    <LinkButton size="small" onClick={onBuyMore}>
                      Buy more targets
                    </LinkButton>
                  </>
                ) : null}
              </span>
            ) : null}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Outside the collapsible: a staged deletion must not hide with it. */}
      {removed.length > 0 ? (
        <div className={removalAlert} role="status">
          <span>
            {removed.map((entry) => entry.name).join(', ')}{' '}
            {removed.length === 1 ? 'leaves' : 'leave'} {streamName} when you
            save, and stops reaching every list using it.
          </span>
          <LinkButton size="small" onClick={restore}>
            Restore
          </LinkButton>
        </div>
      ) : null}
    </div>
  );
};
